// Canonical versions of the legal documents that the user sees at signup.
// IMPORTANT: these must exactly match the values in
// backend/src/Massrestaurant.Core/Constants/LegalDocumentVersions.cs
// The backend rejects the registration if the versions don't match.
export const TERMS_VERSION = '2026-04-10';
export const PRIVACY_VERSION = '2026-04-10';

// NOTA LEGAL IMPORTANTE:
// Un abogado debe revisar estos documentos antes de producción.

export const termsContent = `# Términos y Condiciones de Uso

**Versión:** ${TERMS_VERSION}

## 1. Definiciones

- **Plataforma / Servicio**: el sistema de gestión para restaurantes Massrestaurant, incluyendo sus módulos de punto de venta (POS), display de cocina (KDS), facturación electrónica, inventarios, reportes y todos los componentes asociados.
- **Massrestaurant**: el proveedor del Servicio.
- **Usuario**: la persona física que crea una cuenta en la Plataforma.
- **Organización**: la entidad jurídica o negocio a nombre del cual se contrata el Servicio.
- **Restaurante**: cada establecimiento operado por la Organización y administrado dentro de la Plataforma.
- **Suscripción**: el plan contratado (prueba gratuita o plan pago) que determina los límites y funciones disponibles.

El Usuario que crea la cuenta declara tener la capacidad legal y la autorización suficiente para obligar a la Organización a estos Términos. La Organización será solidariamente responsable por las acciones realizadas por cualquier Usuario que sea creado o invitado dentro de la Plataforma bajo su cuenta.

## 2. Objeto del Servicio

Massrestaurant proporciona una plataforma SaaS (software como servicio) para la gestión operativa de restaurantes y establecimientos de alimentos y bebidas. El Servicio incluye, entre otras funciones, administración de órdenes, mesas, menús, cocina, inventarios, clientes, facturación electrónica conforme a la normativa fiscal aplicable en México, y reportes.

**Facturación electrónica (CFDI).** Massrestaurant actúa únicamente como un intermediario tecnológico para la generación y timbrado de Comprobantes Fiscales Digitales por Internet (CFDI). El Usuario es el único responsable de la veracidad y exactitud de los datos fiscales ingresados (RFC, régimen fiscal, uso de CFDI, domicilio fiscal, conceptos, montos, etc.) y de la correcta emisión de los comprobantes conforme a su actividad y a las disposiciones aplicables. Massrestaurant no asume responsabilidad por multas, recargos, créditos fiscales, actualizaciones o cualquier perjuicio derivado de errores en los datos proporcionados por el Usuario, ni por rechazos del SAT o del Proveedor Autorizado de Certificación (PAC) por causas ajenas a la disponibilidad del servicio de timbrado.

## 3. Registro y Cuenta

3.1. El Usuario se compromete a proporcionar información veraz, completa y actualizada al momento del registro y durante todo el uso del Servicio.

3.2. El Usuario es el único responsable de mantener la confidencialidad de sus credenciales de acceso y de todas las actividades que ocurran bajo su cuenta.

3.3. Massrestaurant puede suspender o cancelar cuentas que contengan información falsa o que se utilicen en violación de estos Términos.

## 4. Período de Prueba, Planes de Pago y Reembolsos

4.1. Al registrarte, obtienes acceso a un **período de prueba gratuito de 7 días** con funcionalidad limitada. No se requiere tarjeta de crédito durante la prueba.

4.2. Al finalizar el período de prueba, el Usuario podrá contratar un plan de pago para continuar usando el Servicio. Los detalles, precios y características de cada plan se publican en la Plataforma y pueden actualizarse de tiempo en tiempo.

4.3. Los pagos se procesan a través de proveedores externos (por ejemplo, Stripe) sujetos a sus propios términos.

4.4. **Política de reembolsos.** Los planes de pago son facturados por adelantado. **No se realizarán reembolsos por períodos parcialmente utilizados**, salvo que lo exija la ley aplicable. El Usuario puede cancelar su Suscripción en cualquier momento, y el Servicio continuará activo hasta el final del período ya pagado, sin generar cargos adicionales por los períodos posteriores.

## 5. Uso Aceptable

El Usuario se compromete a NO:

- Utilizar el Servicio para fines ilegales, fraudulentos o que violen derechos de terceros.
- Intentar vulnerar la seguridad del Servicio, acceder a cuentas ajenas, o realizar ingeniería inversa del software.
- Sobrecargar deliberadamente la infraestructura, enviar spam, malware o contenido dañino.
- Revender, sublicenciar o redistribuir el Servicio sin autorización escrita de Massrestaurant.

## 6. Propiedad Intelectual

Todos los derechos de propiedad intelectual sobre el Servicio, incluyendo el código fuente, diseño, logotipos, marcas y documentación, pertenecen a Massrestaurant o a sus licenciantes. El Usuario recibe una licencia limitada, no exclusiva, intransferible y revocable para usar el Servicio durante la vigencia de su Suscripción.

Los datos capturados por el Usuario en la Plataforma (productos, clientes, órdenes, ventas, etc.) son propiedad del Usuario/Organización. Massrestaurant los trata conforme al Aviso de Privacidad.

## 7. Disponibilidad del Servicio, Respaldos y Limitación de Responsabilidad

7.1. Massrestaurant hará sus mejores esfuerzos para mantener el Servicio disponible 24/7, pero no garantiza un servicio ininterrumpido. Se podrán realizar ventanas de mantenimiento notificadas con razonable antelación.

7.2. **Respaldos del Usuario.** Es responsabilidad del Usuario realizar respaldos periódicos de sus datos mediante las herramientas de exportación disponibles en la Plataforma. Aunque Massrestaurant implementa medidas razonables de respaldo en su infraestructura, no sustituye los respaldos que el Usuario debe mantener para sus propios fines operativos, fiscales y contables.

7.3. EN LA MÁXIMA MEDIDA PERMITIDA POR LA LEY, MASSRESTAURANT NO SERÁ RESPONSABLE POR DAÑOS INDIRECTOS, INCIDENTALES, ESPECIALES O CONSECUENCIALES, INCLUYENDO PÉRDIDA DE INGRESOS, UTILIDADES, CLIENTELA O DATOS, DERIVADOS DEL USO O IMPOSIBILIDAD DE USO DEL SERVICIO.

7.4. La responsabilidad total acumulada de Massrestaurant frente al Usuario por cualquier reclamo derivado del Servicio no excederá el monto pagado por el Usuario por concepto de Suscripción durante los tres (3) meses anteriores al hecho que originó el reclamo.

## 8. Terminación

8.1. El Usuario puede cancelar su cuenta en cualquier momento desde el panel de administración o solicitándolo por escrito a soporte.

8.2. **Incumplimiento y falta de pago.** Massrestaurant puede suspender o cancelar la cuenta del Usuario por incumplimiento material de estos Términos, previa notificación razonable cuando sea posible. El impago de la Suscripción dentro de los cinco (5) días hábiles posteriores a la fecha de vencimiento será considerado incumplimiento material y dará lugar a la suspensión inmediata del acceso a la Plataforma, sin perjuicio del cobro de los montos adeudados.

8.3. **Eliminación de datos tras terminación.** Tras la terminación (por cancelación, incumplimiento o cualquier otra causa), el Usuario dispondrá de **treinta (30) días naturales** contados a partir de la fecha efectiva de terminación para exportar sus datos a través de las herramientas de exportación de la Plataforma. Transcurrido dicho plazo sin solicitud expresa de extensión aceptada por Massrestaurant, Massrestaurant podrá eliminar permanentemente todos los datos asociados a la cuenta, salvo aquellos que deba conservar por obligación legal (por ejemplo, información fiscal en los plazos que marca la ley).

## 9. Modificaciones a los Términos

Massrestaurant puede modificar estos Términos de tiempo en tiempo. La versión vigente se publicará en la Plataforma junto con la fecha de vigencia. Los cambios significativos se comunicarán al Usuario con razonable antelación. El uso continuado del Servicio después de la entrada en vigor de los cambios implica la aceptación de los nuevos Términos.

## 10. Ley Aplicable y Jurisdicción

Estos Términos se rigen por las leyes de los Estados Unidos Mexicanos. Para la interpretación y cumplimiento, las partes se someten a la jurisdicción de los tribunales competentes en México, renunciando expresamente a cualquier otro fuero que pudiera corresponderles.

## 11. Contacto

Para cualquier duda sobre estos Términos, contacta a Massrestaurant a través de los canales de soporte publicados en la Plataforma.
`;

export const privacyContent = `# Aviso de Privacidad

**Versión:** ${PRIVACY_VERSION}

En cumplimiento a la Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP), su Reglamento y demás disposiciones aplicables, **Massrestaurant** pone a disposición del Usuario el presente Aviso de Privacidad.

## 1. Identidad y Domicilio del Responsable

**Massrestaurant** (en adelante, "el Responsable") es el responsable del tratamiento de los datos personales que recaba a través de la Plataforma.

**Domicilio del Responsable para efectos del presente Aviso:** Retorno 1, número 3, Colonia El Espinal, C.P. 94330, Orizaba, Veracruz, México.

## 2. Datos Personales que se Recaban

El Responsable podrá recabar los siguientes datos personales del Usuario:

- **Datos de identificación**: nombre, apellidos, correo electrónico.
- **Datos de contacto**: correo electrónico, teléfono (si se proporciona).
- **Datos de facturación**: nombre o razón social, RFC, dirección fiscal (cuando el Usuario contrata planes de pago o utiliza el módulo de facturación).
- **Datos técnicos**: dirección IP, tipo de navegador, sistema operativo, páginas visitadas dentro de la Plataforma, registros de acceso.
- **Datos de pago**: procesados directamente por el proveedor de pagos (Stripe u otro). Massrestaurant no almacena datos completos de tarjetas.

## 3. Finalidades del Tratamiento

**Finalidades primarias** (necesarias para la relación):

- Crear y administrar la cuenta del Usuario y la Organización.
- Prestar el Servicio contratado.
- Facturar los servicios contratados.
- Atender solicitudes de soporte técnico.
- Cumplir obligaciones legales y fiscales.

**Finalidades secundarias** (no necesarias, el Usuario puede oponerse):

- Enviar comunicaciones sobre nuevas funciones, mejoras o promociones del Servicio.
- Realizar estadísticas y análisis internos para mejorar la Plataforma.

**Oposición al tratamiento para finalidades secundarias.** Si el Usuario no desea que sus datos se traten para las finalidades secundarias, puede enviar un correo a **info@massresto.com** con el asunto **"OPOSICIÓN FINALIDADES SECUNDARIAS"**, indicando su nombre completo y el correo electrónico con el que está registrado en la Plataforma. Massrestaurant cesará el envío de comunicaciones promocionales dentro de los **cinco (5) días hábiles** siguientes a la recepción de la solicitud. La negativa al uso de los datos para finalidades secundarias no será motivo para negar los servicios contratados.

## 4. Fundamento Legal

El tratamiento de los datos personales se realiza con base en el consentimiento del Usuario y en la necesidad de ejecutar la relación contractual entre el Responsable y el Usuario.

## 5. Transferencias de Datos

El Responsable puede transferir datos personales a los siguientes terceros, únicamente para las finalidades descritas:

- **Proveedores de infraestructura en la nube** para alojamiento del Servicio.
- **Proveedores de pagos** (como Stripe) para procesar pagos.
- **Proveedor de facturación electrónica (PAC)** para generar CFDIs ante el SAT cuando el Usuario utiliza el módulo de facturación.
- **Autoridades competentes** cuando así lo requiera la ley.

El Responsable no comercializa los datos personales del Usuario con terceros.

## 6. Derechos ARCO

El Usuario tiene derecho a **Acceder, Rectificar, Cancelar u Oponerse** al tratamiento de sus datos personales (derechos ARCO), así como a revocar el consentimiento otorgado.

Para ejercer estos derechos, el Usuario deberá enviar una solicitud al correo **info@massresto.com** incluyendo:

- Nombre completo y correo electrónico con el que se registró en la Plataforma.
- Descripción clara del derecho que desea ejercer.
- Documento oficial que acredite su identidad.

**Plazo de respuesta.** El Responsable atenderá las solicitudes ARCO en un plazo máximo de **veinte (20) días hábiles** contados a partir de la recepción de la solicitud. Este plazo podrá ser prorrogado por un período igual cuando así lo justifiquen las circunstancias del caso, lo cual será notificado al Usuario dentro del plazo original.

## 7. Medios para Ejercer los Derechos

Las solicitudes ARCO, preguntas y comentarios sobre este Aviso pueden enviarse al correo señalado en la sección anterior o a través de los canales de contacto publicados en la Plataforma.

## 8. Uso de Cookies y Tecnologías Similares

La Plataforma utiliza cookies y tecnologías similares estrictamente con fines operativos: mantener la sesión del Usuario, recordar preferencias de interfaz y realizar análisis internos de uso. **No se utilizan cookies de publicidad comportamental ni se comparten datos con redes publicitarias de terceros.** El Usuario puede configurar su navegador para rechazar cookies consultando la ayuda oficial de su navegador (Chrome, Firefox, Edge, Safari, etc.); sin embargo, algunas funciones del Servicio podrían dejar de operar correctamente.

## 9. Conservación de Datos

Los datos personales se conservarán mientras la cuenta del Usuario esté activa y hasta treinta (30) días naturales después de su cancelación, salvo instrucción en contrario del Usuario o requerimiento legal.

**Excepción fiscal.** Los datos relacionados con facturación electrónica (CFDIs, información fiscal del emisor y del receptor, montos, conceptos) se conservarán por un período mínimo de **cinco (5) años** para cumplir con las obligaciones establecidas por el Código Fiscal de la Federación y demás disposiciones fiscales aplicables.

## 10. Medidas de Seguridad

El Responsable implementa medidas administrativas, técnicas y físicas razonables para proteger los datos personales contra daño, pérdida, alteración, destrucción o uso no autorizado.

## 11. Cambios al Aviso de Privacidad

El presente Aviso puede sufrir modificaciones. Las versiones actualizadas se publicarán en la Plataforma indicando la fecha de vigencia. El Responsable informará al Usuario de cambios significativos a través del correo electrónico registrado.

## 12. Consentimiento

Al marcar la casilla correspondiente y completar el registro en la Plataforma, el Usuario manifiesta haber leído y entendido el presente Aviso de Privacidad y otorga su consentimiento expreso para el tratamiento de sus datos personales en los términos aquí descritos.
`;
